import { useState, useEffect, useContext } from "react";
import {
  Button,
  Input,
  Form,
  Select,
  Space,
  Row,
  Col,
  Card,
  Skeleton,
  message,
  Modal,
} from "antd";
import {
  StopOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

import {
  addPayAtPaymentApi,
  resendPayAtSmsApi,
  regeneratePayAtNumberApi,
  RegeneratePayAtNumberRequest,
} from "../../../services/Payment/PaymentApis";
import { getMemberApi } from "../../../services/Member/MemberApis";
import { AuthContext } from "../../../context/AuthContext";
import NupayPaymentInfo from "./NupayPaymentInfo";

interface StepProps {
  buttonLoading: boolean;
  gotoStep: Function;
  form: any;
  update: boolean;
  submit: Function;
  memberId: any;
}

const tailLayout = {
  labelCol: { span: 8 },
  wrapperCol: { offset: 0, span: 24 },
};

const PayAtPaymentInfo: React.FC<StepProps> = ({
  buttonLoading,
  gotoStep,
  form,
  update,
  submit,
  memberId,
}) => {
  const { currentUser } = useContext(AuthContext);
  const [card, setCard] = useState<any>([]);
  const [debiCheckPayment, setDebiCheckPayment] = useState<any>(null);
  const [showDebiCheckForm, setShowDebiCheckForm] = useState(false);

  // const [paymentId, setPaymentId] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [member, setMember] = useState();

  const delay = (ms: any) => new Promise((resolve) => setTimeout(resolve, ms));

  const addPayAt = async () => {
    setLoading(true);

    // Call mandate instruction
    const paymentApiResponse = await addPayAtPaymentApi(memberId);

    if (!paymentApiResponse.success) {
      setLoading(false);
      message.error(
        `${paymentApiResponse.message}. Payment could not be captured, please review information and try again.`
      );
      throw new Error("Adding payment details failed.");
    }

    // If successful create card and set Mandate to Pending
    const newCard = {
      id: card.length + 1,
      ...paymentApiResponse.data,
    };

    setCard((prevCard: any) => [...prevCard, newCard]);

    setLoading(false);
    message.success("Pay@ payment successfully added.");
  };

  const resendPayAtSms = async () => {
    setLoading(true);

    try {
      const response = await resendPayAtSmsApi(String(memberId));

      if (!response.success) {
        setLoading(false);
        message.error(
          response.message || "Failed to resend Pay@ SMS. Please try again."
        );
        return;
      }

      setLoading(false);
      message.success("Pay@ SMS resent successfully.");
    } catch (error) {
      setLoading(false);
      message.error("An error occurred while resending Pay@ SMS.");
    }
  };

  const regeneratePayAtNumber = async () => {
    Modal.confirm({
      title: "Regenerate Pay@ Number?",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p style={{ fontSize: "14px", marginBottom: "12px" }}>
            <strong>⚠️ Warning:</strong> If you regenerate the Pay@ number,
            the previous number will no longer work.
          </p>
          <p style={{ fontSize: "14px" }}>Are you sure you want to continue?</p>
        </div>
      ),
      okText: "Yes, Regenerate",
      okType: "danger",
      okButtonProps: {
        style: {
          backgroundColor: "#ff4d4f",
          borderColor: "#ff4d4f",
          color: "#fff",
        },
      },
      cancelText: "Cancel",
      onOk: async () => {
        setLoading(true);

        try {
          const payload: RegeneratePayAtNumberRequest = {
            confirmedBy: currentUser?.id || 0,
            userConfirmed: true,
          };

          const response = await regeneratePayAtNumberApi(
            String(memberId),
            payload
          );

          if (!response.success) {
            setLoading(false);
            message.error(
              response.message ||
                "Failed to regenerate Pay@ number. Please try again."
            );
            return;
          }

          // Update the card with the new Pay@ number
          const updatedCard = {
            ...card[0],
            PayAtAccountNumber: response.data.newAccountNumber,
          };

          setCard([updatedCard]);

          setLoading(false);
          message.success(
            `Pay@ number regenerated successfully. New number: ${response.data.newAccountNumber}`
          );
        } catch (error) {
          setLoading(false);
          message.error("An error occurred while regenerating Pay@ number.");
        }
      },
    });
  };

  useEffect(() => {
    getMemberApi(String(memberId))
      .then((res: any) => {
        const primary = res.data.PaymentType;
        const secondary = res.data.SecondaryPaymentType;
        const payAt =
          primary?.Type === "PayAt"
            ? primary
            : secondary?.Type === "PayAt"
            ? secondary
            : null;
        const debiCheck =
          primary?.Type === "DebiCheck"
            ? primary
            : secondary?.Type === "DebiCheck"
            ? secondary
            : null;

        if (payAt != null) {
          setCard(() => [payAt]);
        }
        setDebiCheckPayment(debiCheck || null);
        setMember(res.data);
      })
      .catch((err: any) => {
        console.log(err);
      });
  }, []);

  const content = (
    <>
      <Row>
        <Col span={10} offset={7}>
          <h3 style={{ marginBottom: "16px" }}>Current Payment Status</h3>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              marginBottom: "16px",
            }}
          >
            {!loading ? (
              card.map((card: any) => (
                <Card
                  key={card && card.id}
                  title={
                    <div>
                      {card && card.PayAtAccountNumber ? (
                        <>
                          <span style={{ color: "Green" }}>
                            Pay@ Account Number - {card.PayAtAccountNumber}
                          </span>
                        </>
                      ) : (
                        <>
                          <span style={{ color: "Red" }}>
                            Pay@ Account Number not available
                          </span>
                        </>
                      )}
                    </div>
                  }
                  style={{ marginBottom: 16 }}
                ></Card>
              ))
            ) : (
              <Card>
                <Skeleton active />
              </Card>
            )}
            {debiCheckPayment && (
              <Card
                title="Second payment method - DebiCheck"
                style={{ marginBottom: 16 }}
              >
                <p>
                  Mandate status -{" "}
                  <strong>
                    {debiCheckPayment.MandateInstructionStatus ||
                      debiCheckPayment.MandateStatus ||
                      "Pending"}
                  </strong>
                </p>
              </Card>
            )}
          </div>

          <Form.Item {...tailLayout}>
            {card.length === 0 ? (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  loading={loading}
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  onClick={addPayAt}
                >
                  <span>Add</span>
                </Button>
              </Space>
            ) : (
              <div style={{ width: "100%", marginBottom: 10, display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <Button
                  loading={loading}
                  size="large"
                  style={{ 
                    flex: 1, 
                    backgroundColor: "#1890ff", 
                    color: "white", 
                    borderColor: "#1890ff",
                    minWidth: 0
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = "#40a9ff";
                      e.currentTarget.style.borderColor = "#40a9ff";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = "#1890ff";
                      e.currentTarget.style.borderColor = "#1890ff";
                    }
                  }}
                  onClick={resendPayAtSms}
                >
                  <span>SMS Pay@ Info</span>
                </Button>
                <Button
                  loading={loading}
                  size="large"
                  style={{ 
                    flex: 1, 
                    backgroundColor: "#ff4d4f", 
                    color: "white", 
                    borderColor: "#ff4d4f",
                    minWidth: 0
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = "#ff7875";
                      e.currentTarget.style.borderColor = "#ff7875";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.currentTarget.style.backgroundColor = "#ff4d4f";
                      e.currentTarget.style.borderColor = "#ff4d4f";
                    }
                  }}
                  onClick={regeneratePayAtNumber}
                >
                  <span>Recreate Pay@</span>
                </Button>
                {!debiCheckPayment && (
                  <Button
                    loading={loading}
                    size="large"
                    block
                    style={{ marginTop: 8 }}
                    onClick={() => setShowDebiCheckForm(true)}
                  >
                    <span>Add 2nd Payment Method</span>
                  </Button>
                )}
              </div>
            )}
          </Form.Item>

          <Form.Item {...tailLayout}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                loading={buttonLoading}
                style={{ marginBottom: 10 }}
                block
                size="large"
                onClick={() => {
                  submit().then((result: any) => {
                    if (result) {
                      gotoStep(2);
                    }
                  });
                }}
              >
                Okay
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
      {showDebiCheckForm && (
        <div style={{ marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Add DebiCheck as 2nd payment method</h3>
          <NupayPaymentInfo
            buttonLoading={buttonLoading}
            gotoStep={gotoStep}
            form={form}
            update={false}
            submit={submit}
            memberId={memberId}
          />
        </div>
      )}
    </>
  );

  return content;
};

export default PayAtPaymentInfo;
